import type { ApolloClient, OperationVariables, TypedDocumentNode } from '@apollo/client';
import { parse, visit } from 'graphql';
import type { DocumentNode, FieldNode, FragmentDefinitionNode, OperationDefinitionNode } from 'graphql';
import { cloneDeep, memoize } from 'lodash-es';

import type QueryMethods from 'components/core/createModify/interfaces/queryMethods';
import type TableCellData from 'components/ui/tables/interfaces/tableCellData';

import LoggingService from 'components/core/logging/LoggingService';
import { convertedNestedString } from 'utils/stringUtils';

/**
 * Given a gql document and payload, filter out any unneeded fields in the payload that aren't used in the
 * gql document. This prevents extra fields from being sent in a gql query/mutation request.
 * @param query - the document created with the gql template literal
 * @param payload - the payload (ie variables) used as arguments to the gql document
 * @param warn - boolean, to console.warn when an unnecessary field is found (true by default on dev build)
 */

export const gqlFilterFields = <TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  payload: TVariables | undefined,
  warn = process.env.NODE_ENV === 'development'
) => {
  const definition = query.definitions?.[0] as OperationDefinitionNode;
  const variableNames = definition?.variableDefinitions?.map(({ variable }) => variable.name.value);

  if (!variableNames) {
    return payload;
  }

  const payloadCopy = cloneDeep(payload);
  const unusedFields: string[] = [];

  for (const field in payloadCopy) {
    if (!payloadCopy.hasOwnProperty(field) || variableNames.includes(field)) {
      continue;
    }

    delete payloadCopy[field];

    unusedFields.push(field);
  }

  if (warn && unusedFields.length > 0) {
    LoggingService.warn({
      message: `Query "${definition.name?.value}" has unnecessary fields: "${unusedFields.join(', ')}"`,
    });
  }

  return payloadCopy;
};
/**
 * Given a query, table columns, and static columns, this util will return the variables for the query based on
 * whether or not the table column is enabled/disabled and whether or not the table column is static. Table columns
 * that are static are ignored, and if the table column id is not in the list of the query paramaters, it will
 * also be ignored.
 * @param query - The container query
 * @param tableColumns - The column configuration
 * @param staticColumns - Fields always requested which are the minimum mandatory fields needed by container queries
 * defined by `xItemList fragments e.g. retailItemList`.
 * Thus, staticColumns must always contain at minimum all fields in `xItemList`.
 * @template TVariables - The query variables type for documentation purposes
 * @returns An object with dynamic string keys (column variable names) and boolean values
 */

export const gqlFormatTableColumnFields = (
  query: DocumentNode,
  tableColumns: TableCellData[],
  staticColumns: string[]
): Record<string, any> => {
  const definition = query.definitions?.[0] as OperationDefinitionNode;
  const variableNames = definition?.variableDefinitions?.map(({ variable }) => variable.name.value);
  const results: Record<string, boolean> = {};

  if (!variableNames) {
    return results as Record<string, any>;
  }

  for (const column of tableColumns) {
    const columnId = convertedNestedString(column.columnId);
    if (!staticColumns.includes(column.columnId) && variableNames.includes(`d_${columnId}On`)) {
      results[`d_${columnId}On`] = column.enabled;
    }
  }

  return results as Record<string, any>;
};

export const normalizeRefetchQueries = memoize((queries: QueryMethods['refetchQueries']): string[] | undefined =>
  queries?.map(item => item.query)
);

export const checkForMatchingRefetchQueries = (
  queries: QueryMethods['refetchQueries']
): Pick<ApolloClient.MutateOptions, 'onQueryUpdated' | 'refetchQueries'> => {
  const refetchQueries = normalizeRefetchQueries(queries);

  return {
    onQueryUpdated: (observableQuery, diff) => {
      /**
       * Look for matching refetch queries. If `true`, refetch from network.
       *
       * IMPORTANT: We catch refetch errors to prevent them from propagating to the
       * mutation caller. When Apollo's onQueryUpdated returns a Promise, it waits for
       * that promise and aggregates any errors with the mutation result. This causes
       * issues like a 403 error from a refetch (e.g., SettingsNavigationItemCountQuery)
       * appearing as if the mutation itself failed.
       *
       * By catching errors here, we:
       * 1. Allow the mutation to report its own success/failure accurately
       * 2. Let refetch errors be handled by the query's own error handling (useQuery)
       * 3. Prevent unrelated permission errors from blocking successful mutations
       */
      const queryName = observableQuery?.queryName || '';
      const isMatchingRefetchQueries = !!refetchQueries?.includes(queryName || '');

      if (isMatchingRefetchQueries) {
        return observableQuery.refetch().catch(error => {
          if (process.env.NODE_ENV === 'development') {
            // oxlint-disable-next-line no-console -- development-only Apollo diagnostic
            console.warn(`[Apollo] Refetch failed for "${queryName}":`, error);
          }
        });
      }

      if (!diff.complete && diff.missing) {
        if (process.env.NODE_ENV === 'development') {
          // oxlint-disable-next-line no-console -- development-only Apollo diagnostic
          console.warn(diff.missing);
        }

        return observableQuery.refetch().catch(error => {
          if (process.env.NODE_ENV === 'development') {
            // oxlint-disable-next-line no-console -- development-only Apollo diagnostic
            console.warn(`[Apollo] Refetch failed for "${queryName}":`, error);
          }
        });
      }

      return diff.result;
    },
    refetchQueries,
  };
};

/**
 * Extracts field paths from a GraphQL query string or DocumentNode in dot notation.
 * This function parses the GraphQL AST and resolves all fragments into their constituent fields.
 *
 * **Special handling:**
 * - Fields named "data" are omitted from paths (common alias in mutations/queries)
 * - Meta fields like `__typename` are skipped
 * - Fragments are resolved into individual fields
 *
 * @param query - The GraphQL query string or DocumentNode
 * @returns Array of sorted, deduplicated field paths (e.g., ['user.name', 'user.address.city'])
 *
 * @example
 * const query = `
 *   query {
 *     user {
 *       name
 *       address {
 *         city
 *       }
 *     }
 *   }
 * `;
 * const paths = extractFieldPaths(query);
 * // Returns: ['user.address.city', 'user.name']
 *
 * @example
 * // With DocumentNode
 * import { gql } from '@apollo/client';
 * const query = gql`
 *   query {
 *     user {
 *       name
 *     }
 *   }
 * `;
 * const paths = extractFieldPaths(query);
 * // Returns: ['user.name']
 *
 * @example
 * // Fragment resolution
 * const queryWithFragment = `
 *   fragment UserFields on User {
 *     name
 *     email
 *   }
 *   query {
 *     user {
 *       ...UserFields
 *       age
 *     }
 *   }
 * `;
 * const paths = extractFieldPaths(queryWithFragment);
 * // Returns: ['user.age', 'user.email', 'user.name']
 *
 * @example
 * // "data" field omission
 * const mutation = `
 *   mutation {
 *     data: createUser(input: { name: "John" }) {
 *       id
 *       name
 *     }
 *   }
 * `;
 * const paths = extractFieldPaths(mutation);
 * // Returns: ['id', 'name'] (note: 'data' is omitted)
 */
export const extractFieldPaths = (query: DocumentNode | string): string[] => {
  const ast = typeof query === 'string' ? parse(query) : query;
  const paths: string[] = [];
  const fragments = new Map<string, FragmentDefinitionNode>();

  visit(ast, {
    FragmentDefinition(node) {
      fragments.set(node.name.value, node);
    },
  });

  const processSelectionSet = (selectionSet: any, pathStack: string[]) => {
    visit(selectionSet, {
      Field: {
        enter(node: FieldNode) {
          const fieldName = node.alias?.value ?? node.name.value;

          if (fieldName.startsWith('__')) {
            return false;
          }

          if (fieldName === 'data') {
            return;
          }

          pathStack.push(fieldName);

          if (!node.selectionSet || node.selectionSet.selections.length === 0) {
            paths.push(pathStack.join('.'));
          }
        },
        leave(node: FieldNode) {
          const fieldName = node.alias?.value ?? node.name.value;

          if (fieldName !== 'data') {
            pathStack.pop();
          }
        },
      },
      FragmentSpread(node) {
        const fragmentName = node.name.value;
        const fragment = fragments.get(fragmentName);

        if (fragment && fragment.selectionSet) {
          processSelectionSet(fragment.selectionSet, pathStack);
        }

        return false;
      },
    });
  };

  visit(ast, {
    OperationDefinition(node) {
      if (node.selectionSet) {
        processSelectionSet(node.selectionSet, []);
      }

      return false;
    },
  });

  return [...new Set(paths)].toSorted();
};
